<template>
  <!-- Banner -->
  <section>
    <div v-if="!!widgetData?.mobileImageUrl || !!widgetData?.imageUrl">
      <div
        v-if="!!widgetData?.mobileImageUrl || !!widgetData?.imageUrl"
        :key="widgetData?.mobileImageUrl"
        class="relative block bg-primary-tangerine lg:hidden"
      >
        <img
          :src="widgetData?.mobileImageUrl || widgetData?.imageUrl"
          :alt="
            widgetData?.headerEn?.toLowerCase() ??
            widgetData?.headerTh?.toLowerCase() ??
            'Products thumbnail'
          "
          class="top-0 left-0 object-cover object-center w-full h-full"
        />
      </div>
      <div
        v-if="!!widgetData?.imageUrl"
        :key="widgetData?.imageUrl"
        class="relative hidden bg-primary-tangerine lg:block"
      >
        <img
          :src="widgetData?.imageUrl"
          :alt="
            widgetData?.headerEn?.toLowerCase() ??
            widgetData?.headerTh?.toLowerCase() ??
            'Products thumbnail'
          "
          class="top-0 left-0 object-cover object-center w-full h-full"
        />
      </div>
    </div>
    <div v-else :class="'bg-primary-tangerine relative'">
      <img
        alt="all styles"
        src="/images/shop-all-styles.jpg"
        class="top-0 left-0 object-cover object-center w-full h-full"
      />
    </div>
  </section>

  <ProductListHeader
    v-if="
      widgetData &&
      (widgetData.smallImageUrl ||
        (locale === 'en' ? widgetData.titleEn : widgetData.titleTh) ||
        (locale === 'en' ? widgetData.descriptionEn : widgetData.descriptionTh))
    "
    :image-url="widgetData.smallImageUrl"
    :title="locale === 'en' ? widgetData.titleEn : widgetData.titleTh"
    :description="
      locale === 'en' ? widgetData.descriptionEn : widgetData.descriptionTh
    "
  />

  <!-- Breadcrumbs -->
  <BaseContainer>
    <section class="flex py-4 gap-x-3">
      <NuxtLinkLocale to="/">{{ $t('products.home') }}</NuxtLinkLocale>
      <div>/</div>
      <NuxtLinkLocale to="/products">
        {{ $t('products.allProducts') }}
      </NuxtLinkLocale>
      <div v-if="pageData?.name && !search">/</div>
      <NuxtLinkLocale
        v-if="pageData?.name && !search"
        :to="`/products/${category}`"
      >
        {{ locale === 'en' ? pageData?.name : pageData?.name }}
      </NuxtLinkLocale>
      <template v-if="!!search">
        <div>/</div>
        <div>{{ $t('products.search') }}: {{ search }}</div>
      </template>
    </section>
  </BaseContainer>

  <div v-if="!isDesktop" class="px-6 pt-6 pb-4 lg:hidden">
    <V2ProductListingProductSelector :page-type="pageType" />
  </div>

  <V2ProductListingSort
    :is-sorting-open="isSortingOpen"
    :hits="hits"
    :title="pageData?.name || $t('products.allProducts')"
    @close="handleCloseSort"
  />

  <template v-if="!isDesktop">
    <section class="grid grid-cols-2 px-4 gap-x-4 lg:hidden">
      <div
        class="rounded-full bg-gray-10 py-[10px] font-medium flex items-center justify-center cursor-pointer"
        @click="handleOpenFilter"
      >
        <div>{{ $t('products.filterBy') }}</div>
        <Icon name="bars-filter" class="text-gray-50" />
      </div>
      <div
        class="rounded-full bg-gray-10 py-[10px] font-medium flex items-center justify-center cursor-pointer"
        @click="handleOpenSort"
      >
        <div>{{ $t('products.sortBy') }}</div>
        <Icon name="sort" class="text-gray-50" />
      </div>
    </section>
  </template>

  <BaseContainer>
    <section ref="productListEl" class="flex" aria-label="Product Listing">
      <ProductListSidebarFilter
        :is-filter-open="isFilterOpen"
        :facet-data="facetData"
        @close="handleCloseFilter"
        @clear-all-filter="handleClearAllFilter"
      />

      <!-- Product Grid & Pagination -->
      <main
        class="flex flex-col flex-1 py-4 lg:py-10 lg:pl-5 min-[1230px]:pl-10 gap-y-4 min-[1230px]:gap-y-8"
      >
        <V2ProductListingProductSelector
          v-if="isDesktop"
          :page-type="pageType"
        />

        <!-- Product Items -->
        <section
          class="grid grid-cols-2 lg:grid-cols-3 gap-x-4 lg:gap-x-8 xl:gap-x-[52px] gap-y-8"
          aria-label="Product Items"
        >
          <template
            v-if="
              searchResult &&
              Array.isArray(searchResult?.hits) &&
              searchResult.hits.length > 0
            "
          >
            <ProductListItemThumbnailComponent
              v-for="item in searchResult.hits"
              :key="item.objectID"
              product-listing
              :image-url="item.thumbnail_image_url"
              :name="
                locale === 'en'
                  ? item.display_name_en
                  : item.display_name_th || item.display_name_en
              "
              :price="item.price"
              :tags="[
                ...item.artwork_tags_mapping,
                ...item.product_tag_mapping
              ]"
              :product-slug="item.product_slug"
              :artwork-slug="item.artwork_slug"
              :device-option-available="item.device_option_available"
              :artwork-name="item.artwork_name_en"
            />
          </template>
        </section>

        <!-- Pagination Info & Controls -->
        <ProductListPagination
          v-if="isShowPagination"
          :items-per-page-option="itemsPerPageOption"
          :items-per-page="itemsPerPage"
          :page="page"
          :search-result="searchResult"
          @change="(nextPage) => changePage(nextPage)"
          @change-items-per-page="
            (nextItemsPerPage: number = 15) =>
              changeItemsPerPage(nextItemsPerPage)
          "
        />
        <ContentBelowProductList
          v-if="
            locale === 'en'
              ? dataPage.contentDescriptionEn
                ? true
                : false
              : dataPage.contentDescriptionTh
                ? true
                : false
          "
          :description="dataPage"
        />
      </main>
    </section>
  </BaseContainer>
</template>

<script lang="ts" setup>
// ─── Imports ──────────────────────────────────────────────────────
import scrollLock from 'scroll-lock'
import {
  cloneDeep,
  compact,
  filter,
  flattenDeep,
  groupBy,
  includes,
  keysIn,
  pick,
  startsWith,
  uniq
} from 'lodash-es'
import { seoRepository } from '~/repository/seoRepository'
import { categoryRepository } from '~/repository/v2/categorynameRepository'
import { cmsRepository } from '~/repository/cmsRepository'
import { useProductFilterStore } from '~/stores/productFilter'

// ─── Lazy Components ─────────────────────────────────────────────
const ProductListItemThumbnailComponent = defineAsyncComponent(
  () => import('@/components/v2/ProductListItemThumbnail.vue')
)

// ─── Composables ─────────────────────────────────────────────────
const { locale } = useI18n()
const route = useRoute()
const router = useRouter()
const localePath = useLocalePath()
const config = useRuntimeConfig()
const { $serverApi } = useNuxtApp()
const { isDesktop } = useWindowSize()
const { on } = useEventTracking()
const algoliaIndex = useAlgoliaInitIndex(config.public.algoliaIndexNext)

const seoRepo = seoRepository($serverApi)
const nameRepo = categoryRepository($serverApi)
const cmsRepo = cmsRepository($serverApi)

// ─── Props ───────────────────────────────────────────────
const props = defineProps({
  pageType: String, // e.g. "product", "style", etc.
  slugPageDataAllStatic: String,
  slugPageDataStatic: String
})

// ─── Refs & State ───────────────────────────────────────────────
const isSortingOpen = ref(false)
const isFilterOpen = ref(false)
const productListEl = ref<HTMLElement | null>(null)
const isLoadingFacet = ref(true)

// ─── Filters ─────────────────────────────────────────────────────
const query = computed(() => route.query)
const sort = computed(() => (query.value.sortBy as string) || 'relevent')
const search = computed(() => (query.value.search as string) || '')
const maxPrice = computed(() => query.value['max-price'] as string)
const category = computed(() => route.params.category as string)
const page = computed(() => Number(query.value.page || 1))
const itemsPerPage = computed(() => Number(query.value['items-per-page'] || 15))

// ─── Store ───────────────────────────────────────────────────────
const state = useProductFilterStore()
const { selectedValues, personalizable, facetData } = storeToRefs(state)
const { updateFacetData, clear } = useProductFilterStore()

// ─── Pagination ──────────────────────────────────────────────────
const isShowPagination = computed(() => {
  return searchResult.value && Boolean(searchResult.value?.hits?.length > 1)
})

const itemsPerPageOption = Array.from({ length: 4 }, (_, i) => ({
  label: `${(i + 1) * 15}`,
  value: (i + 1) * 15
}))

const { dataPage } = await usePageDescription(
  props.pageType as 'Devices' | 'Collabs' | 'Products' | 'Styles'
)

const changePage = async (newPage: number | undefined) => {
  if (newPage === page.value) return
  const newQuery = { ...query.value, page: String(newPage) }
  const unwatch = watch(
    () => route.query.page,
    async (val) => {
      if (val === String(newPage)) {
        unwatch()
        await nextTick()
        updateProductList({ init: false })
      }
    }
  )
  await router.push({ query: newQuery })
}

const changeItemsPerPage = async (newItemsPerPage: number | undefined) => {
  if (newItemsPerPage === itemsPerPage.value) return
  const newQuery = { ...query.value, 'items-per-page': String(newItemsPerPage) }
  const unwatch = watch(
    () => route.query['items-per-page'],
    async (val) => {
      if (val === String(newItemsPerPage)) {
        unwatch()
        await nextTick()
        updateProductList({ init: false })
      }
    }
  )
  await router.push({ query: newQuery })
}

// ─── Helpers ─────────────────────────────────────────────────────
const facetFilters = computed(() => {
  const filters: string[][] = []
  const grouped = groupBy(selectedValues.value, 'type')

  Object.entries(grouped).forEach(([key, values]) => {
    filters.push(values.slice(0, 2).map((v) => `${key}:${v.value}`))
  })

  if (personalizable.value || props.pageType === 'personalization') {
    filters.push(['artwork_personalizable:true'])
  }

  if (
    route.path.includes('/products') ||
    (route.path.includes('/devices') && !containsCategoryProduct())
  ) {
    category.value &&
      filters.push([`all_category_product_slug:${category.value}`])
  }

  if (route.path.includes('/styles')) {
    category.value &&
      filters.push([`all_category_artwork_slug:${category.value}`])
  }

  if (route.path.includes('/collabs')) {
    filters.push([`all_category_artwork_slug:${category.value || 'collabs'}`])
  }

  return filters || []
})

const containsCategoryProduct = () =>
  flattenDeep(selectedValues.value).some((item) =>
    item.type.includes('category_product')
  )

// ─── Product List Update ────────────────────────────────────────
const { data: searchResult, refresh: refreshProductList } = await useAsyncData(
  'productListSSR',
  async () => {
    const { result, search: algoliaSearch } =
      useAlgoliaSearch<AlgoliaProductV2>(
        config.public.algoliaIndexNext +
          (sort.value !== 'relevent' ? `_${sort.value}` : '')
      )

    const facetPage = getProductPageUrlBySearch(search.value)
    const shouldRedirect = !includes(facetPage, '/products?search=')
    if (shouldRedirect) {
      router.push(localePath(facetPage))
    } else {
      await algoliaSearch({
        query: search.value,
        requestOptions: {
          page: page.value - 1,
          hitsPerPage: itemsPerPage.value,
          numericFilters: compact([
            maxPrice.value && `price<${maxPrice.value}`
          ]),
          facetFilters: facetFilters.value
        }
      })
    }

    return result.value
  }
)

const hits = computed(() => {
  return searchResult.value?.nbHits || 0
})

const updateProductList = async (
  { init }: { init?: boolean } = { init: false }
) => {
  if (
    !init &&
    !['navigation', 'landing'].includes(route.query.from as string)
  ) {
    const rect = productListEl.value?.getBoundingClientRect()
    rect && window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  await refreshProductList()

  // Tracking event (only on client)
  if (import.meta.client) {
    on('view_item_list', buildTrackingPayload())
  }
}

const buildTrackingPayload = () => ({
  item_category: category.value || 'collabs',
  ...(search.value && { search_term: search.value }),
  ...(maxPrice.value && { search_term: `price under ${maxPrice.value} THB` }),
  artwork_name: uniq(
    compact(searchResult.value?.hits?.map((item) => item.artwork_name_en))
  )
})

const generateQueryObject = (
  filters: { type: string; value: string; name: string }[]
) => {
  const queryObject: Record<string, string | string[]> = {}
  filters.forEach(({ type, name }) => {
    const key = type.replace(/^category_/, '').replace(/_(en|th)$/, '')
    if (queryObject[key]) {
      queryObject[key] = ([] as string[]).concat(queryObject[key], name)
    } else {
      queryObject[key] = name
    }
  })
  return queryObject
}

const persistQueryOnChange = pick(query.value, [
  'sortBy',
  'device_series',
  'device',
  'style',
  'collabs',
  'category',
  'color',
  'max-price',
  'search',
  'by',
  'items-per-page',
  ...filter(keysIn(cloneDeep(route.query)), (key) => startsWith(key, 'utm'))
])

const handleClearAllFilter = () => {
  const persistQuery = pick(query.value, ['sortBy', 'page', 'search'])
  clear()
  router.push({ query: { ...persistQuery } })
  handleCloseFilter()
}

// ─── Watchers ────────────────────────────────────────────────────
watch(
  () => [search.value],
  ([val]) => {
    updateProductList({ init: false })
  }
)

watch(
  () => [selectedValues.value],
  ([val]) => {
    const newQuery = generateQueryObject(flattenDeep(val))
    router.push({
      query: {
        ...newQuery,
        ...persistQueryOnChange,
        ...(personalizable.value ? { personalizable: 'yes' } : {})
      }
    })
    updateProductList({ init: false })
  }
)

watch(
  () => [personalizable.value],
  ([val]) => {
    const newQuery = generateQueryObject(flattenDeep(selectedValues.value))
    router.push({
      query: {
        ...newQuery,
        ...persistQueryOnChange,
        ...(val ? { personalizable: 'yes' } : {})
      }
    })
    updateProductList({ init: false })
  }
)

watch(
  () => [query.value],
  ([val]) => {
    if (Object.keys(val || {}).length === 0) clear()
  }
)

onMounted(async () => {
  if (Object.keys(query.value).length === 0) clear()
  isLoadingFacet.value = true
  try {
    const results = await algoliaIndex.search('', {
      facets: ['*'],
      hitsPerPage: 0
    })

    if (results.facets) {
      updateFacetData(transformFacetData(results.facets))
    } else {
      console.warn('Facet data not found')
    }
  } finally {
    isLoadingFacet.value = false
  }
})

// ─── SEO Data ────────────────────────────────────────────────────
const { data: pageData } = await useAsyncData(
  'getProductsSeo',
  async () => {
    const res = await nameRepo.getCategoryName(
      category.value || props.pageType || 'products',
      locale.value
    )

    return res.data
  },
  { watch: [category] }
)
const { data: pageDataAllStatic } = await useAsyncData(
  'getSeoProductByAllProductPage',
  () => seoRepo.getSeo(props.slugPageDataAllStatic || 'product-by-product-all')
)
const { data: pageDataStatic } = await useAsyncData(
  'getSeoProductByProductPage',
  () => seoRepo.getSeo(props.slugPageDataStatic || 'product-by-product')
)

function replacePlaceholders<T extends Record<string, string>>(
  template: string,
  data: T
): string {
  return template.replace(/{(.*?)}/g, (_, key: string) => data[key] || '')
}

function getLocalizedTemplate(
  meta: { en?: string; th?: string } | undefined,
  locale: string
): string {
  return locale === 'en' ? (meta?.en ?? '') : (meta?.th ?? meta?.en ?? '')
}

function getReplacedSeoMeta(
  meta: { en?: string; th?: string } | undefined,
  locale: string,
  data: Record<string, string> | null | undefined
): string {
  const template = getLocalizedTemplate(meta, locale)
  return replacePlaceholders(template, data ?? {})
}

const seoTitle = computed(() =>
  getReplacedSeoMeta(
    pageDataStatic.value?.meta_title,
    locale.value,
    pageData.value
  )
)

const seoDescription = computed(() =>
  getReplacedSeoMeta(
    pageDataStatic.value?.meta_description,
    locale.value,
    pageData.value
  )
)

const seoTitleAll = computed(() =>
  getLocalizedTemplate(pageDataAllStatic.value?.meta_title, locale.value)
)

const seoDescriptionAll = computed(() =>
  getLocalizedTemplate(pageDataAllStatic.value?.meta_description, locale.value)
)

const schemaProductListPage = () => {
  const itemListElement = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${config.public.siteUrl}`
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'All Products',
      item: `${config.public.siteUrl}/products`
    }
  ]
  if (pageData.value?.name && !search.value) {
    itemListElement.push({
      '@type': 'ListItem',
      position: 3,
      name: pageData.value?.name ?? '',
      item: `${config.public.siteUrl}${route.fullPath}`
    })
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageData.value?.name ?? '',
    url: `${config.public.siteUrl}${route.fullPath}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement
    }
  }
}

const schemaItemList = () => {
  const hits = searchResult.value?.hits
  if (!hits) return null

  const products = hits.map((hit) => {
    return {
      '@type': 'Product',
      name:
        locale.value === 'en'
          ? hit.display_name_en
          : hit.display_name_th || hit.display_name_en,
      url: `${config.public.siteUrl}${getProductUrlV2(hit.product_slug, hit.artwork_slug)}`,
      image: `${config.public.mediaUrl}${hit.thumbnail_image_url}`,
      offers: {
        '@type': 'Offer',
        price: hit.price,
        priceCurrency: 'THB'
      }
    }
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'ProductList',
    itemListElement: products
  }
}

const getSchemaOrg = () => {
  const productListPage = schemaProductListPage()
  const itemList = schemaItemList()
  const schemaOrg = []
  if (productListPage) {
    schemaOrg.push(productListPage)
  }
  if (itemList) {
    schemaOrg.push(itemList)
  }
  return schemaOrg
}

useSeoMeta({
  title: category.value ? seoTitle.value : seoTitleAll.value,
  description: category.value ? seoDescription.value : seoDescriptionAll.value,
  ogImage: category.value
    ? pageDataStatic.value?.og_image
    : pageDataAllStatic.value?.og_image,
  ogImageSecureUrl: category.value
    ? pageDataStatic.value?.og_image
    : pageDataAllStatic.value?.og_image,
  keywords: pageData.value?.name
    ? pageDataStatic.value?.meta_keyword
    : pageDataAllStatic.value?.meta_keyword
})

useSchemaOrg(getSchemaOrg())

// ─── CMS Banner ─────────────────────────────────────────────────
const { data: bannerData } = useAsyncData('getBanners/' + route.fullPath, () =>
  cmsRepo.getWidget('banners')
)

const widgetData = computed(() => {
  const items = bannerData.value?.data.data.items || []
  const withPaths = items.map((item: { pathname: string }) => ({
    ...item,
    pathname: item.pathname?.split(',').map((p) => p.trim())
  }))
  const bannerIndex = findMostSimilarPath(withPaths, route.fullPath)
  return bannerIndex !== null ? items[bannerIndex] : null
})

// ─── UI Interaction ─────────────────────────────────────────────
const handleOpenSort = () => {
  isSortingOpen.value = true
  scrollLock.disablePageScroll()
}
const handleCloseSort = () => {
  isSortingOpen.value = false
  scrollLock.enablePageScroll()
}
const handleOpenFilter = () => {
  isFilterOpen.value = true
  scrollLock.disablePageScroll()
}
const handleCloseFilter = () => {
  isFilterOpen.value = false
  scrollLock.enablePageScroll()
}
</script>
